import { Label, Panel, Switch, Textbox, PanelProps, Splitter, SplitterOrientation } from "@mcleod/components";
import { ExtendedDataType, HorizontalAlignment, MetadataField } from "@mcleod/core";
import { DesignerTool } from "./DesignerTool";
import { UIDesigner } from "./UIDesigner";

export class DesignerToolsPanel extends Panel {
  inputTableSearch: Textbox;
  inputFieldSearch: Textbox;
  switchDragMode: Switch;
  splitter: Splitter;
  panelMainTools: Panel;
  panelDataSourceTools: Panel;
  panelSplitBottom: Panel;
  designer: UIDesigner;

  constructor(designer: UIDesigner, props?: Partial<PanelProps>) {
    super({ padding: 0, rowBreak: false, borderRightWidth: 1, borderRightColor: "strokeSecondary", fillHeight: true, scrollY: true, ...props });
    this.designer = designer;
    this.splitter = new Splitter({ id: "splitter", fillRow: true, fillHeight: true, orientation: SplitterOrientation.vertical, position: "50%" });
    this.panelMainTools = new Panel({ id: "panelMainTools", fillRow: true, fillHeight: true, scrollY: true });
    this.add(new Label({ caption: "Tools", backgroundColor: "primary", align: HorizontalAlignment.CENTER, fillRow: true, color: "primary.reverse" }));
    this.panelMainTools.add(new DesignerTool(designer, "Label"));
    this.panelMainTools.add(new DesignerTool(designer, "Textbox"));
    this.panelMainTools.add(new DesignerTool(designer, "Button"));
    this.panelMainTools.add(new DesignerTool(designer, "Checkbox"));
    this.panelMainTools.add(new DesignerTool(designer, "Switch"));
    this.panelMainTools.add(new DesignerTool(designer, "Table"));
    this.panelMainTools.add(new DesignerTool(designer, "Image"));
    this.panelMainTools.add(new DesignerTool(designer, "CityState"));
    this.panelMainTools.add(new DesignerTool(designer, "StyledEditor"));
    this.panelMainTools.add(new DesignerTool(designer, "Splitter"));
    this.panelMainTools.add(new DesignerTool(designer, "Tree"));
    this.panelMainTools.add(new DesignerTool(designer, "Map"));
    this.panelMainTools.add(new DesignerTool(designer, "Panel"));
    this.panelMainTools.add(new DesignerTool(designer, "Stepper"));
    this.panelMainTools.add(new DesignerTool(designer, "Tabset"));
    this.panelMainTools.add(new DesignerTool(designer, "List"));
    this.panelMainTools.add(new DesignerTool(designer, "Attachment"));
    this.panelMainTools.add(new DesignerTool(designer, "Layout", { isNested: true }));
    this.panelMainTools.add(new DesignerTool(designer, "HorizontalSpacer"));
    this.panelMainTools.add(new DesignerTool(designer, "SearchButton"));
    this.panelMainTools.add(new DesignerTool(designer, "SaveButton"));
    this.panelMainTools.add(new DesignerTool(designer, "Widget"));
    this.panelMainTools.add(new DesignerTool(designer, "DataHeader"));
    this.panelMainTools.add(new DesignerTool(designer, "NumberEditor"));
    this.panelMainTools.add(new DesignerTool(designer, "StarRating"));
    this.panelMainTools.add(new DesignerTool(designer, "Chart"));
    //this.panelMainTools.add(new DesignerTool(designer, "GaugeChart"));

    this.panelSplitBottom = new Panel({ fillRow: true, fillHeight: true });
    this.inputTableSearch = new Textbox({ id: "inputTableSearch", captionVisible: false, placeholder: "Search table", warningLabelVisible: false, fillRow: true, marginLeft: 8, marginRight: 8, rowBreak: false });
    this.inputTableSearch.addChangeListener(event => this.displayDataSourceTools());
    this.panelSplitBottom.add(this.inputTableSearch);

    this.inputFieldSearch = new Textbox({ id: "inputFieldSearch", captionVisible: false, placeholder: "Search field", fillRow: true, warningLabelVisible: false, marginRight: 8 });
    this.inputFieldSearch.addChangeListener(event => this.displayDataSourceTools());
    this.panelSplitBottom.add(this.inputFieldSearch);
    this.switchDragMode = new Switch({ id: "switchDrag", caption: "Drag mode", leftCaption: "Editable", rightCaption: "Read-only", color: "subtle.darker", marginLeft: 12, marginBottom: 8, fillRow: true, marginRight: 8 });
    this.switchDragMode.addChangeListener(event => this.displayDataSourceTools());
    this.panelSplitBottom.add(this.switchDragMode);
    this.panelSplitBottom.add(new Panel({ height: 1, padding: 0, margin: 8, fillRow: true, backgroundColor: "subtle.light" }));
    this.panelDataSourceTools = new Panel({ id: "dsTools", fillRow: true, padding: 0, fillHeight: true, scrollX: false, scrollY: true });
    this.panelSplitBottom.add(this.panelDataSourceTools);
    this.splitter.add(this.panelMainTools);
    this.splitter.add(this.panelSplitBottom);
    this.add(this.splitter);

  }

  displayDataSourceTools() {
    const tab = this.designer.getActiveTab();
    this.panelDataSourceTools.removeAll();
    if (tab == null || tab.lastSelectedDataSource == null)
      this.panelDataSourceTools.add(new Label({ caption: "Select a datasource to see available fields", color: "subtle.light", align: HorizontalAlignment.CENTER }));
    else {
      tab.lastSelectedDataSource.getMetadata().then(metadata => {
        if (metadata != null) {
          const fields = metadata.output;
          const searchTable = this.inputTableSearch.text.trim().toLowerCase();
          const searchField = this.inputFieldSearch.text.trim().toLowerCase();
          const sorted = Object.keys(fields).sort();
          for (const fieldName of sorted) {
            const props = fields[fieldName];
            const matchesTable = searchTable.length === 0
              || (props.tableAlias?.toLowerCase().indexOf(searchTable) >= 0)
              || (props.tableName?.toLowerCase().indexOf(searchTable) >= 0);
            const matchesField = searchField.length === 0 || (fieldName.toLowerCase().indexOf(searchField) >= 0);
            if (matchesTable && matchesField) {
              const field = fields[fieldName];
              const compType = field.extDataType === ExtendedDataType.YES_NO ? "Checkbox" : "Textbox";
              this.panelDataSourceTools.add(new DesignerTool(this.designer, compType, new MetadataField({ ...props, name: fieldName })));
            }
          }
        }
      });
    }
  }
}
